/**
 * FlightHoverCard — hover ANY flight number anywhere in the app and see what it is (Matt,
 * 2026-10-17: "anytime a flight number is shown, if I hover my mouse over it I get an info popup of
 * what it is").
 *
 * One listener for the whole app, mounted once in App.jsx: no component has to opt in. On a short
 * hover pause it reads the word under the pointer (caretRangeFromPoint), and if it looks like a
 * flight number (letters then 2–4 digits, e.g. PDR105, CFW101) asks the server what flight that is
 * (api/flights.js#flightLookup). Unknown words cache as "not a flight" so a sheet number or anything
 * else that merely looks like one never shows a card. An element may also carry data-flight="PDR105"
 * (for SVG labels the caret lookup can't read).
 *
 * One message first: the card leads with what the flight is and where it stands in one line.
 */
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { lookupFlight } from '../services/flights.js'

const PATTERN = /^[A-Z]{2,8}\d{2,4}$/
const cache = new Map()          // number -> flight | null | Promise
const fmt = (s) => s ? new Date(String(s).slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''

function wordAt(x, y) {
  let node = null, offset = 0
  if (document.caretRangeFromPoint) {
    const r = document.caretRangeFromPoint(x, y); if (r) { node = r.startContainer; offset = r.startOffset }
  } else if (document.caretPositionFromPoint) {
    const p = document.caretPositionFromPoint(x, y); if (p) { node = p.offsetNode; offset = p.offset }
  }
  if (!node || node.nodeType !== 3) return null
  const t = node.textContent || ''
  let a = offset, b = offset
  while (a > 0 && /[A-Za-z0-9]/.test(t[a - 1])) a--
  while (b < t.length && /[A-Za-z0-9]/.test(t[b])) b++
  const w = t.slice(a, b)
  if (!PATTERN.test(w)) return null
  // The pointer must really be over the word, not just nearest to it.
  const range = document.createRange(); range.setStart(node, a); range.setEnd(node, b)
  const rect = range.getBoundingClientRect()
  if (x < rect.left - 2 || x > rect.right + 2 || y < rect.top - 2 || y > rect.bottom + 2) return null
  return { word: w, rect }
}

async function get(number) {
  if (cache.has(number)) return cache.get(number)
  const p = lookupFlight(number).then(f => { cache.set(number, f); return f }).catch(() => { cache.delete(number); return null })
  cache.set(number, p)
  return p
}

function where(f) {
  if (f.landedAt) return f.landedReason === 'posted' ? `Landed ${fmt(f.landedAt)} · posted` : `Pulled from the air ${fmt(f.landedAt)}`
  if (f.waitingSince) return `In the air · waiting since ${fmt(f.waitingSince)}`
  const pct = f.hill == null ? null : Math.round(f.hill * 100)
  return `In the air${pct != null ? ` · ${pct}% along` : ''}${f.daysRemaining != null && f.daysRemaining > 0 ? ` · ${f.daysRemaining} day${f.daysRemaining === 1 ? '' : 's'} of work left` : ''}`
}

export default function FlightHoverCard() {
  const [card, setCard] = useState(null)   // { flight, rect }
  const timer = useRef(null)
  const shownFor = useRef(null)

  useEffect(() => {
    let lastX = 0, lastY = 0
    function hide() { clearTimeout(timer.current); shownFor.current = null; setCard(null) }
    function onMove(e) {
      lastX = e.clientX; lastY = e.clientY
      // Leaving the word (or the card) hides it; a short pause over a new word shows it.
      if (shownFor.current) {
        const r = shownFor.current
        if (lastX < r.left - 6 || lastX > r.right + 6 || lastY < r.top - 6 || lastY > r.bottom + 6) {
          if (!(e.target && e.target.closest && e.target.closest('.aj-fhc'))) hide()
        }
      }
      clearTimeout(timer.current)
      timer.current = setTimeout(async () => {
        const tagged = e.target && e.target.closest && e.target.closest('[data-flight]')
        const hit = tagged ? { word: tagged.getAttribute('data-flight'), rect: tagged.getBoundingClientRect() } : wordAt(lastX, lastY)
        if (!hit || !hit.word) return
        const f = await get(hit.word.toUpperCase())
        if (!f) return
        shownFor.current = hit.rect
        setCard({ flight: f, rect: hit.rect })
      }, 380)
    }
    window.addEventListener('mousemove', onMove, { passive: true })
    window.addEventListener('scroll', hide, true)
    window.addEventListener('keydown', hide)
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('scroll', hide, true); window.removeEventListener('keydown', hide); clearTimeout(timer.current) }
  }, [])

  if (!card) return null
  const f = card.flight, r = card.rect
  const W = 300
  const left = Math.max(12, Math.min(r.left, window.innerWidth - W - 12))
  const below = r.bottom + 220 < window.innerHeight
  const captain = f.crew.find(c => c.captain)
  const others = f.crew.filter(c => !c.captain)
  return createPortal(
    <div className="aj-fhc" role="tooltip" style={{
      position: 'fixed', left, top: below ? r.bottom + 8 : undefined, bottom: below ? undefined : window.innerHeight - r.top + 8,
      width: W, zIndex: 2147483600, background: 'var(--aj-bg-elevated, #fff)', color: 'var(--aj-text, #1d1d1f)',
      border: '0.5px solid var(--aj-border-mid, rgba(0,0,0,.12))', borderRadius: 14, padding: '13px 15px 12px',
      boxShadow: '0 14px 36px rgba(0,0,0,.18)', fontFamily: '"DM Sans", system-ui, sans-serif', fontSize: 13, lineHeight: 1.45,
      animation: 'ajFhcIn .16s ease both',
    }}>
      <style>{'@keyframes ajFhcIn{from{opacity:0;transform:translateY(-3px)}to{opacity:1;transform:none}}'}</style>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span style={{ fontFamily: '"DM Mono", monospace', fontSize: 12.5, color: 'var(--aj-text-muted, #6e6e73)' }}>{f.number}</span>
        <span style={{ fontSize: 14.5, fontWeight: 600, minWidth: 0 }}>{f.milestoneName || (f.kind === 'squadron' ? 'Squadron' : 'Flight')}</span>
      </div>
      <div style={{ color: 'var(--aj-text-mid, #3a3a3c)', marginTop: 2 }}>{f.projectName}{f.phase ? ` · ${f.phase.replace(/\s*Phase$/, '')}` : ''}</div>
      <div style={{ marginTop: 9, fontWeight: 600, color: f.landedAt ? 'var(--aj-text-muted, #6e6e73)' : f.waitingSince ? '#c4610b' : 'var(--aj-text, #1d1d1f)' }}>{where(f)}</div>
      {!f.landedAt && f.hill != null && (
        <div style={{ height: 4, borderRadius: 2, background: 'var(--aj-bg-muted, #ececf0)', marginTop: 6, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: Math.round(f.hill * 100) + '%', background: f.waitingSince ? '#f28c28' : 'var(--aj-text, #1d1d1f)', borderRadius: 2 }} />
        </div>
      )}
      <div style={{ marginTop: 9, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '3px 10px', fontSize: 12.5 }}>
        <span style={{ color: 'var(--aj-text-muted, #6e6e73)' }}>Crew</span>
        <span>{f.crew.length ? [captain && `${captain.name} (captain)`, ...others.map(c => c.name)].filter(Boolean).join(', ') : 'Nobody crewed'}</span>
        {f.due && !f.landedAt && <><span style={{ color: 'var(--aj-text-muted, #6e6e73)' }}>Due</span><span>{fmt(f.due)}</span></>}
        <span style={{ color: 'var(--aj-text-muted, #6e6e73)' }}>Launched</span><span>{fmt(f.launched)}</span>
        {f.openRequests > 0 && <><span style={{ color: 'var(--aj-text-muted, #6e6e73)' }}>Waiting on</span><span>{f.openRequests} open request{f.openRequests === 1 ? '' : 's'}</span></>}
      </div>
      {f.lastReport && f.lastReport.note && !f.landedAt && (
        <div style={{ marginTop: 9, paddingTop: 8, borderTop: '0.5px solid var(--aj-border, rgba(0,0,0,.08))', fontSize: 12.5, color: 'var(--aj-text-mid, #3a3a3c)' }}>
          <span style={{ color: 'var(--aj-text-muted, #6e6e73)' }}>{fmt(f.lastReport.date)}{f.lastReport.by ? ` · ${f.lastReport.by.split(' ')[0]}` : ''}: </span>{f.lastReport.note}
        </div>
      )}
    </div>,
    document.body
  )
}
